/**
 * @architecture-header 1
 * @layer module
 * @domain curriculum
 * @responsibility Connect Curriculum query and mutation state to independent Desktop and Mobile surfaces.
 * @state-owner TanStack Query Curriculum units and mutation state
 * @allowed-imports module query, mutation, public surfaces; authentication context; browser media API
 * @forbidden routes; shared shell composition; direct API transport
 * @public-api CurriculumUnitBoard and CurriculumTab
 * @side-effects query loading, retry, and authorized Curriculum mutations
 * @tests CurriculumUnitBoard.test.tsx
 */

import { useEffect, useState } from 'react';
import { useActiveContext } from '../../auth';
import { useCreateCurriculumUnit, useUpdateCurriculumUnit } from '../api/curriculum.mutations';
import { useCurriculumUnits, useKnowledgeComponents } from '../api/curriculum.queries';
import {
  useCreateKnowledgeComponent,
  useUpdateKnowledgeComponent,
} from '../api/knowledge-components.mutations';
import type { CurriculumOrganizationKnowledgeComponent, CurriculumUnit } from '../model/types';
import {
  CurriculumDesktopSurface,
  CurriculumMobileSurface,
  type CurriculumSurfaceStatus,
  type CurriculumUnitPatch,
} from './CurriculumUnitSurface';
import {
  KnowledgeComponentDesktopSurface,
  KnowledgeComponentMobileSurface,
} from './KnowledgeComponentDirectorySurface';

function mediaSurface(): 'desktop' | 'mobile' {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'desktop';
  return window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop';
}

function useResponsiveSurface(): 'desktop' | 'mobile' {
  const [surface, setSurface] = useState<'desktop' | 'mobile'>(mediaSurface);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia('(max-width: 767px)');
    const update = () => setSurface(query.matches ? 'mobile' : 'desktop');
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);
  return surface;
}

function queryStatus(query: {
  readonly isPending?: boolean;
  readonly isLoading?: boolean;
  readonly isError?: boolean;
  readonly error: unknown;
  readonly data?: readonly unknown[];
}): CurriculumSurfaceStatus {
  if (query.isPending || query.isLoading) return 'loading';
  if (query.isError)
    return (query.error as { status?: number } | null)?.status === 403 ? 'denied' : 'error';
  return query.data?.length ? 'ready' : 'empty';
}

export function CurriculumUnitBoard({
  activeContextId,
  organizationId,
  classroomId,
  surface,
}: {
  readonly activeContextId: string;
  readonly organizationId: string;
  readonly classroomId: string;
  readonly surface: 'desktop' | 'mobile';
}) {
  const [showingArchived, setShowingArchived] = useState(false);
  const [showingArchivedKnowledgeComponents, setShowingArchivedKnowledgeComponents] =
    useState(false);
  const [knowledgeComponentSearch, setKnowledgeComponentSearch] = useState('');
  const active = useCurriculumUnits(activeContextId, classroomId, 'active');
  const archived = useCurriculumUnits(activeContextId, classroomId, 'archived');
  const create = useCreateCurriculumUnit();
  const update = useUpdateCurriculumUnit();
  const knowledgeComponents = useKnowledgeComponents(
    activeContextId,
    organizationId,
    'active',
    knowledgeComponentSearch,
  );
  const archivedKnowledgeComponents = useKnowledgeComponents(
    activeContextId,
    organizationId,
    'archived',
    knowledgeComponentSearch,
  );
  const createKnowledgeComponent = useCreateKnowledgeComponent();
  const updateKnowledgeComponent = useUpdateKnowledgeComponent();
  const activeQuery = showingArchived ? archived : active;
  const Surface = surface === 'desktop' ? CurriculumDesktopSurface : CurriculumMobileSurface;
  const KnowledgeSurface =
    surface === 'desktop' ? KnowledgeComponentDesktopSurface : KnowledgeComponentMobileSurface;
  const idempotencyKey = (action: string) =>
    `agent-a-cur-f01-ui-${action}-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const onCreate = (name: string, knowledgeComponentIds: readonly string[]) => {
    create.mutate({
      activeContextId,
      organizationId,
      classroomId,
      name,
      knowledgeComponentIds,
      idempotencyKey: idempotencyKey('create'),
    });
  };
  const onUpdate = (unit: CurriculumUnit, patch: CurriculumUnitPatch) => {
    update.mutate({
      activeContextId,
      organizationId,
      classroomId,
      unit,
      ...patch,
      idempotencyKey: idempotencyKey(unit.archived ? 'reopen' : 'update'),
    });
  };
  const onCreateKnowledgeComponent = (input: {
    name: string;
    standardCode: string;
    description: string;
  }) => {
    createKnowledgeComponent.mutate({
      activeContextId,
      organizationId,
      ...input,
      idempotencyKey: `agent-a-cur-f03-ui-create-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    });
  };
  const onUpdateKnowledgeComponent = (
    component: CurriculumOrganizationKnowledgeComponent,
    patch: {
      name?: string;
      standardCode?: string | null;
      description?: string;
      archived?: boolean;
    },
  ) => {
    updateKnowledgeComponent.mutate({
      activeContextId,
      organizationId,
      component,
      ...patch,
      idempotencyKey: `agent-a-cur-f03-ui-update-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    });
  };
  const knowledgeQuery = showingArchivedKnowledgeComponents
    ? archivedKnowledgeComponents
    : knowledgeComponents;
  return (
    <>
      <Surface
        archivedUnits={archived.data}
        mutationError={create.error ?? update.error}
        mutationPending={create.isPending || update.isPending}
        onCreate={onCreate}
        onRetry={() => {
          void active.refetch();
          void archived.refetch();
        }}
        onShowArchived={setShowingArchived}
        onUpdate={onUpdate}
        showingArchived={showingArchived}
        status={queryStatus(activeQuery)}
        units={active.data}
      />
      <KnowledgeSurface
        archivedComponents={archivedKnowledgeComponents.data}
        components={knowledgeComponents.data}
        mutationError={createKnowledgeComponent.error ?? updateKnowledgeComponent.error}
        mutationPending={createKnowledgeComponent.isPending || updateKnowledgeComponent.isPending}
        onCreate={onCreateKnowledgeComponent}
        onRetry={() => {
          void knowledgeComponents.refetch();
          void archivedKnowledgeComponents.refetch();
        }}
        onSearch={setKnowledgeComponentSearch}
        onShowArchived={setShowingArchivedKnowledgeComponents}
        onUpdate={onUpdateKnowledgeComponent}
        search={knowledgeComponentSearch}
        showingArchived={showingArchivedKnowledgeComponents}
        status={queryStatus(knowledgeQuery)}
      />
    </>
  );
}

export function CurriculumTab({
  classroomId,
  surface,
}: {
  readonly classroomId: string;
  readonly surface?: 'desktop' | 'mobile';
}) {
  const context = useActiveContext();
  const responsiveSurface = useResponsiveSurface();
  return (
    <CurriculumUnitBoard
      activeContextId={context.data?.id ?? 'inactive'}
      classroomId={classroomId}
      organizationId={context.data?.organizationId ?? 'demo-organization'}
      surface={surface ?? responsiveSurface}
    />
  );
}
